/**
 * File Upload Configuration Context
 * Provides file upload settings to all components
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import fileUploadConfigService, { FileUploadConfig } from '../services/fileUploadConfigService';
import { useAuth } from './AuthContext';

interface FileUploadConfigContextType {
  config: FileUploadConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
  validateFile: (file: File) => Promise<string | null>;
  formatFileSize: (bytes: number) => string;
}

const FileUploadConfigContext = createContext<FileUploadConfigContextType | undefined>(undefined);

// Default configuration for immediate use before API call completes
const DEFAULT_CONFIG: FileUploadConfig = {
  max_file_size_bytes: 10 * 1024 * 1024,
  max_file_size_mb: 10,
  max_files_per_transaction: 20,
  allowed_extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv', 'jpg', 'jpeg', 'png', 'gif'],
  file_uploads_enabled: true,
};

interface FileUploadConfigProviderProps {
  children: ReactNode;
}

export const FileUploadConfigProvider: React.FC<FileUploadConfigProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [config, setConfig] = useState<FileUploadConfig | null>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fileUploadConfigService.getConfig();
      setConfig(data);
    } catch (err) {
      console.error('Failed to load file upload configuration:', err);
      setError('Failed to load file upload configuration');
      // Keep using default config on error
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadConfig();
    } else {
      // Reset to default config when not authenticated
      setConfig(DEFAULT_CONFIG);
      setLoading(false);
    }
  }, [loadConfig, isAuthenticated]);

  const refreshConfig = useCallback(async () => {
    fileUploadConfigService.clearCache();
    await loadConfig();
  }, [loadConfig]);

  const validateFile = useCallback(async (file: File): Promise<string | null> => {
    return fileUploadConfigService.validateFile(file);
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    return fileUploadConfigService.formatFileSize(bytes);
  }, []);

  return (
    <FileUploadConfigContext.Provider
      value={{
        config,
        loading,
        error,
        refreshConfig,
        validateFile,
        formatFileSize,
      }}
    >
      {children}
    </FileUploadConfigContext.Provider>
  );
};

export const useFileUploadConfig = (): FileUploadConfigContextType => {
  const context = useContext(FileUploadConfigContext);
  if (context === undefined) {
    throw new Error('useFileUploadConfig must be used within a FileUploadConfigProvider');
  }
  return context;
};

export default FileUploadConfigContext;
